import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, Loader2, Mail, Phone, Building } from "lucide-react";

/**
 * BLOC 19/10 — Super-admin : demandes d'accès reçues ("Demander un accès").
 *
 * Permet à Christophe de voir les prospects + marquer "traité". Pas de
 * suppression (règle Wérko trace). Les demandes forwardées vers le tenant
 * Wérko (si SIGNUP_REQUESTS_TENANT_SLUG configuré) apparaissent aussi là.
 */
export default function AccessRequestsAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [processing, setProcessing] = useState({});

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/signup-access-requests?only_pending=${onlyPending}`,
      );
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("Accès réservé aux super-admins Wérko.");
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyPending]);

  async function markProcessed(id) {
    setProcessing((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/admin/signup-access-requests/${id}/mark-processed`);
      toast.success("Demande marquée comme traitée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing((p) => ({ ...p, [id]: false }));
    }
  }

  if (error) {
    return (
      <div className="p-6" data-testid="requests-error">
        <div className="mx-auto max-w-xl rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-red-200">
          <AlertTriangle className="mb-2 h-6 w-6" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="access-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-100">
            <Mail className="h-6 w-6 text-[#C4553B]" />
            Demandes d&apos;accès reçues
          </h1>
          <p className="text-sm text-slate-400">
            Formulaires "Demander un accès" soumis depuis la page publique.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={onlyPending ? "default" : "outline"}
            onClick={() => setOnlyPending(true)}
            className={
              onlyPending
                ? "bg-[#C4553B] text-white"
                : "border-slate-700 bg-slate-800 text-slate-200"
            }
            data-testid="filter-pending"
          >
            En attente
          </Button>
          <Button
            variant={!onlyPending ? "default" : "outline"}
            onClick={() => setOnlyPending(false)}
            className={
              !onlyPending
                ? "bg-[#C4553B] text-white"
                : "border-slate-700 bg-slate-800 text-slate-200"
            }
            data-testid="filter-all"
          >
            Toutes
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="p-10 text-center text-sm text-slate-400">
            {onlyPending ? "Aucune demande en attente." : "Aucune demande reçue."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((req) => (
            <Card
              key={req.id}
              className="border-slate-800 bg-slate-900/40"
              data-testid={`request-${req.id}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-100">
                        {req.first_name} {req.last_name}
                      </h3>
                      <Badge className="border-slate-600 bg-slate-800 text-xs">
                        {req.trade}
                      </Badge>
                      {req.processed_at ? (
                        <Badge className="border-emerald-700 bg-emerald-900/40 text-emerald-200 text-xs">
                          Traité
                        </Badge>
                      ) : (
                        <Badge className="border-amber-700 bg-amber-900/40 text-amber-200 text-xs">
                          En attente
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1">
                      <Building className="h-3.5 w-3.5" /> {req.company_name}
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    {new Date(req.created_at).toLocaleString("fr-BE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <a
                    href={`mailto:${req.email}`}
                    className="flex items-center gap-2 text-slate-300 hover:text-[#C4553B]"
                  >
                    <Mail className="h-4 w-4" /> {req.email}
                  </a>
                  <a
                    href={`tel:${req.phone}`}
                    className="flex items-center gap-2 text-slate-300 hover:text-[#C4553B]"
                  >
                    <Phone className="h-4 w-4" /> {req.phone}
                  </a>
                </div>
                {req.message && (
                  <p className="rounded border border-slate-700 bg-slate-950/40 p-2 text-sm text-slate-300">
                    {req.message}
                  </p>
                )}
                {req.forwarded_lead_id && (
                  <p className="text-xs text-emerald-400">
                    ✓ Forwardé en lead Wérko (id {req.forwarded_lead_id.slice(0, 8)}…)
                  </p>
                )}
                {!req.processed_at && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => markProcessed(req.id)}
                      disabled={!!processing[req.id]}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`mark-processed-${req.id}`}
                    >
                      {processing[req.id] ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="mr-2 h-4 w-4" />
                      )}
                      Marquer traité
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
